import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input, Label, Select } from './Input';
import { ImageUpload } from './ImageUpload';
import { useToast } from './Toast';
// MIGRATION FIREBASE: Utilise Firebase direct au lieu de l'API Python
import { productsService, categoriesService, optionsService } from '../services/firebaseService';
import { deleteField } from 'firebase/firestore';
import { ChevronUp, ChevronDown, X, Info, Search } from 'lucide-react';
import { ProductARSection } from './ProductARSection';
import { isStandby } from '../featureStandby';

const EU_ALLERGENS = [
  'Gluten', 'Crustaces', 'Oeufs', 'Poisson', 'Arachides', 'Soja', 'Lait',
  'Fruits a coque', 'Celeri', 'Moutarde', 'Sesame', 'Sulfites', 'Lupin', 'Mollusques',
];

const SPICY_LABELS = ['Pas piquant', 'Doux', 'Medium', 'Fort'];

export const ProductModal = ({ isOpen, onClose, product, onSuccess }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [options, setOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showOptionsDetails, setShowOptionsDetails] = useState({});
  const [optionSearchQuery, setOptionSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    description_courte: '',
    ingredients: '',
    allergens: [],
    spicy_level: 0,
    base_price: '',
    vat_rate: '10',
    image: '',
    image_url: '',
    tags: [],
    option_ids: [],
    is_available: true,
    is_out_of_stock: false,
    stock_status: null, // '2h', 'today', 'indefinite'
    stock_resume_at: null, // ISO date string pour reprise automatique
  });

  useEffect(() => {
    loadCategories();
    loadOptions();
  }, []);

  // Recharger les catégories et options à chaque ouverture du modal
  useEffect(() => {
    if (isOpen) {
      loadCategories();
      loadOptions();
    }
  }, [isOpen]);

  useEffect(() => {
    if (product) {
      const productData = {
        name: product.name || '',
        category: product.category || '',
        description: product.description || '',
        description_courte: product.description_courte || '',
        ingredients: Array.isArray(product.ingredients) ? product.ingredients.join(', ') : (product.ingredients || ''),
        allergens: product.allergens || [],
        spicy_level: product.spicy_level ?? 0,
        base_price: product.base_price?.toString() || '',
        vat_rate: product.vat_rate?.toString() || '10',
        image: product.image || '',
        image_url: product.image_url || product.image || '',
        tags: product.tags || [],
        option_ids: product.option_ids || [],
        is_available: product.is_available ?? true,
        is_out_of_stock: product.is_out_of_stock ?? false,
        stock_status: product.stock_status || null,
        stock_resume_at: product.stock_resume_at || null,
      };
      setFormData(productData);
      
      // Load selected options with their full details - PRÉSERVER L'ORDRE des option_ids
      if (productData.option_ids.length > 0) {
        // Utiliser map() au lieu de filter() pour conserver l'ordre des option_ids
        const selected = productData.option_ids
          .map(id => options.find(opt => opt.id === id))
          .filter(Boolean) // Retirer les undefined si option non trouvée
          .map((opt, index) => ({ ...opt, order: index }));
        setSelectedOptions(selected);
      }
    } else {
      setFormData({
        name: '',
        category: '',
        description: '',
        description_courte: '',
        ingredients: '',
        allergens: [],
        spicy_level: 0,
        base_price: '',
        vat_rate: '10',
        image: '',
        image_url: '',
        tags: [],
        option_ids: [],
        is_available: true,
        is_out_of_stock: false,
        stock_status: null,
        stock_resume_at: null,
      });
      setSelectedOptions([]);
    }
  }, [product, options]);

  const loadCategories = async () => {
    try {
      // FIREBASE DIRECT
      const result = await categoriesService.getAll();
      setCategories(result.categories || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadOptions = async () => {
    try {
      // FIREBASE DIRECT
      const result = await optionsService.getAll();
      setOptions(result.options || []);
    } catch (error) {
      console.error('Failed to load options:', error);
    }
  };

  const handleOptionToggle = (option, isChecked) => {
    if (isChecked) {
      // Add option to selected
      setSelectedOptions([...selectedOptions, { ...option, order: selectedOptions.length }]);
      setFormData({
        ...formData,
        option_ids: [...formData.option_ids, option.id]
      });
    } else {
      // Remove option
      setSelectedOptions(selectedOptions.filter(opt => opt.id !== option.id));
      setFormData({
        ...formData,
        option_ids: formData.option_ids.filter(id => id !== option.id)
      });
    }
  };

  const moveOptionUp = (index) => {
    if (index === 0) return;
    const newSelected = [...selectedOptions];
    [newSelected[index - 1], newSelected[index]] = [newSelected[index], newSelected[index - 1]];
    setSelectedOptions(newSelected);
    setFormData({
      ...formData,
      option_ids: newSelected.map(opt => opt.id)
    });
  };

  const moveOptionDown = (index) => {
    if (index === selectedOptions.length - 1) return;
    const newSelected = [...selectedOptions];
    [newSelected[index], newSelected[index + 1]] = [newSelected[index + 1], newSelected[index]];
    setSelectedOptions(newSelected);
    setFormData({
      ...formData,
      option_ids: newSelected.map(opt => opt.id)
    });
  };

  const handleAvailabilityChange = (e) => {
    const isAvailable = e.target.checked;
    setFormData({
      ...formData,
      is_available: isAvailable,
      // Si on décoche disponible, on force rupture de stock à false
      is_out_of_stock: isAvailable ? formData.is_out_of_stock : false
    });
  };

  const handleOutOfStockChange = (e) => {
    const isOutOfStock = e.target.checked;
    setFormData({
      ...formData,
      is_out_of_stock: isOutOfStock,
      // Si on coche rupture de stock, on force disponible à true
      is_available: isOutOfStock ? true : formData.is_available
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convertir ingredients string -> array pour Firestore
      const ingredientsArray = formData.ingredients
        ? formData.ingredients.split(',').map(i => i.trim()).filter(Boolean)
        : [];

      const basePayload = {
        name: formData.name,
        category: formData.category,
        description: formData.description,
        description_courte: formData.description_courte || '',
        ingredients: ingredientsArray,
        allergens: formData.allergens || [],
        spicy_level: formData.spicy_level || 0,
        base_price: parseFloat(formData.base_price),
        vat_rate: parseFloat(formData.vat_rate),
        image_url: formData.image_url || formData.image || null,
        tags: formData.tags,
        option_ids: formData.option_ids,
        // Conversion explicite en boolean pour Firestore
        is_available: formData.is_available === true,
        is_out_of_stock: formData.is_out_of_stock === true,
        // FIX: Ajouter stock_status et stock_resume_at pour la gestion rupture temporaire
        stock_status: formData.is_out_of_stock ? (formData.stock_status || 'indefinite') : null,
        stock_resume_at: formData.is_out_of_stock ? formData.stock_resume_at : null,
        option_groups: [],
      };
      console.log('📝 Saving product with is_available:', basePayload.is_available);

      // FIREBASE DIRECT
      if (product) {
        // UPDATE: Supprimer l'ancien champ "options" pour éviter confusion avec option_ids
        const updatePayload = {
          ...basePayload,
          options: deleteField(),
        };
        await productsService.update(product.id, updatePayload);
      } else {
        // CREATE: Pas besoin de deleteField, juste ne pas inclure "options"
        await productsService.create(basePayload);
      }

      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Erreur', error.response?.data?.detail || 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={product ? 'Modifier le produit' : 'Nouveau produit'}
      size="fullscreen"
      headerActions={
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading ? '⏳' : '✓'} {loading ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <div>
            <Label htmlFor="name">Nom du produit *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
        </div>

        <div>
          <Label htmlFor="category">Catégorie *</Label>
          <Select
            id="category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            required
          >
            <option value="">Sélectionner...</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            rows="3"
            placeholder="Description du produit (optionnel)"
          />
        </div>

        {/* --- Section IA / Expert Restaurant --- */}
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-4">
          <h4 className="text-sm font-semibold text-purple-800 flex items-center gap-2">
            <span>🧠</span> Infos pour l'IA vocale
          </h4>

          {/* Description courte */}
          <div>
            <Label htmlFor="description_courte">Description courte (pour l'IA)</Label>
            <Input
              id="description_courte"
              value={formData.description_courte}
              onChange={(e) => setFormData({ ...formData, description_courte: e.target.value })}
              placeholder="Ex: Burger signature avec steak haché frais 150g, cheddar affiné, sauce maison"
              maxLength={200}
            />
            <p className="text-xs text-gray-500 mt-1">{formData.description_courte.length}/200 — L'IA utilise cette phrase pour recommander le produit</p>
          </div>

          {/* Ingrédients */}
          <div>
            <Label htmlFor="ingredients">Ingrédients (séparés par virgule)</Label>
            <Input
              id="ingredients"
              value={formData.ingredients}
              onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })}
              placeholder="Ex: steak haché, cheddar, salade, tomate, oignon, sauce maison"
            />
            <p className="text-xs text-gray-500 mt-1">L'IA pourra répondre aux questions sur la composition</p>
          </div>

          {/* Allergènes (14 UE) */}
          <div>
            <Label>Allergènes (norme UE)</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {EU_ALLERGENS.map((allergen) => {
                const isSelected = formData.allergens.includes(allergen);
                return (
                  <button
                    key={allergen}
                    type="button"
                    onClick={() => {
                      const newAllergens = isSelected
                        ? formData.allergens.filter(a => a !== allergen)
                        : [...formData.allergens, allergen];
                      setFormData({ ...formData, allergens: newAllergens });
                    }}
                    className={`px-3 py-1 text-xs rounded-full font-medium border-2 transition-colors ${
                      isSelected
                        ? 'bg-red-100 border-red-500 text-red-700'
                        : 'bg-white border-gray-300 text-gray-600 hover:border-red-400'
                    }`}
                  >
                    {isSelected ? '⚠️ ' : ''}{allergen}
                  </button>
                );
              })}
            </div>
            {formData.allergens.length > 0 && (
              <p className="text-xs text-red-600 mt-2">⚠️ {formData.allergens.length} allergène(s) : {formData.allergens.join(', ')}</p>
            )}
          </div>

          {/* Niveau de piquant */}
          <div>
            <Label>Niveau de piquant</Label>
            <div className="flex gap-2 mt-1">
              {SPICY_LABELS.map((label, level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setFormData({ ...formData, spicy_level: level })}
                  className={`px-3 py-1.5 text-xs rounded-full font-medium border-2 transition-colors ${
                    formData.spicy_level === level
                      ? 'bg-orange-100 border-orange-500 text-orange-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-orange-400'
                  }`}
                >
                  {'🌶️'.repeat(level) || '—'} {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="base_price">Prix TTC (€) *</Label>
            <Input
              id="base_price"
              type="number"
              step="0.01"
              min="0"
              value={formData.base_price}
              onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="vat_rate">TVA (%) *</Label>
            <Select
              id="vat_rate"
              value={formData.vat_rate}
              onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value })}
              required
            >
              <option value="5.5">5.5%</option>
              <option value="10">10%</option>
              <option value="20">20%</option>
            </Select>
          </div>
        </div>

        <ImageUpload
          currentImage={formData.image_url || formData.image}
          onImageChange={(url) => setFormData({
            ...formData,
            image: url,
            image_url: url
          })}
          label="Image du produit"
        />

        {/* Section AR/3D - juste après l'image.
            En veille (décision propriétaire 2026-08-23) : le panneau est masqué
            et remplacé par un simple avis. Rien n'est supprimé — pour rallumer,
            voir admin/src/featureStandby.js ET functions/featureStandby.js. */}
        {isStandby('ar3d') ? (
          <div className="border border-gray-200 rounded-2xl p-5 bg-gray-50 mt-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 bg-gray-100 rounded-xl">
                <span className="text-xl">⏸️</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 text-lg">Modèle 3D / AR</h3>
                <p className="text-xs text-gray-500">Fonctionnalité en veille</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm text-center py-4">
              La génération 3D / réalité augmentée est temporairement désactivée.
              Les modèles déjà enregistrés sont conservés.
            </p>
          </div>
        ) : product?.id ? (
          <ProductARSection
            product={product}
            onUpdate={onSuccess}
          />
        ) : (
          <div className="border border-gray-200 rounded-2xl p-5 bg-gray-50 mt-6 opacity-60">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl">
                <span className="text-xl">📦</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 text-lg">Modèle 3D / AR</h3>
                <p className="text-xs text-gray-500">Visualisation en réalité augmentée</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm text-center py-4">
              💡 Enregistrez d'abord le produit pour activer la génération 3D/AR
            </p>
          </div>
        )}

        {/* Sélection et organisation des options */}
        <div className="flex-1 flex flex-col min-h-0">
          <Label>Options du produit</Label>
          <p className="text-xs text-gray-500 mb-3">
            Sélectionnez les options que le client pourra choisir pour ce produit
          </p>

          {/* Options sélectionnées avec ordre modifiable */}
          {selectedOptions.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex-shrink-0">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">
                Options sélectionnées ({selectedOptions.length}) - Glissez pour réordonner
              </h4>
              <div className="space-y-2 max-h-52 overflow-y-auto">
                {selectedOptions.map((option, index) => (
                  <div key={option.id} className="bg-white p-3 rounded border border-blue-300">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{option.name}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            option.is_required
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {option.is_required ? 'Obligatoire' : 'Optionnel'}
                          </span>
                        </div>

                        {/* Commentaire interne toujours visible */}
                        {option.internal_comment && (
                          <p className="text-xs text-yellow-700 italic mt-1">
                            📝 {option.internal_comment}
                          </p>
                        )}

                        {/* Bouton pour afficher/masquer les détails */}
                        <button
                          type="button"
                          onClick={() => setShowOptionsDetails({
                            ...showOptionsDetails,
                            [option.id]: !showOptionsDetails[option.id]
                          })}
                          className="text-xs text-blue-600 hover:text-blue-800 mt-1 flex items-center gap-1"
                        >
                          <Info className="w-3 h-3" />
                          {showOptionsDetails[option.id] ? 'Masquer' : 'Voir'} les choix ({option.choices?.length || 0})
                        </button>

                        {/* Détails des choix */}
                        {showOptionsDetails[option.id] && (
                          <div className="mt-2 pl-4 border-l-2 border-blue-200">
                            {option.choices && option.choices.map((choice, idx) => (
                              <div key={idx} className="text-xs text-gray-600 py-1">
                                • {choice.name} {choice.price_modifier > 0 && `(+${choice.price_modifier.toFixed(2)}€)`}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 ml-3">
                        <button
                          type="button"
                          onClick={() => moveOptionUp(index)}
                          disabled={index === 0}
                          className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveOptionDown(index)}
                          disabled={index === selectedOptions.length - 1}
                          className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOptionToggle(option, false)}
                          className="p-1 text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Liste des options disponibles avec recherche */}
          <div className="flex-1 flex flex-col min-h-0 border border-gray-200 rounded-lg overflow-hidden">
            {/* Barre de recherche */}
            <div className="p-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher une option..."
                  value={optionSearchQuery}
                  onChange={(e) => setOptionSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                />
                {optionSearchQuery && (
                  <button
                    type="button"
                    onClick={() => setOptionSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {options.filter(opt => !formData.option_ids.includes(opt.id)).length} options disponibles
                {optionSearchQuery && ` • ${options.filter(opt =>
                  !formData.option_ids.includes(opt.id) &&
                  (opt.name.toLowerCase().includes(optionSearchQuery.toLowerCase()) ||
                   (opt.internal_comment && opt.internal_comment.toLowerCase().includes(optionSearchQuery.toLowerCase())))
                ).length} résultats`}
              </p>
            </div>

            {/* Liste scrollable des options - utilise l'espace disponible */}
            <div className="flex-1 overflow-y-auto p-3 min-h-[250px]">
              <div className="grid grid-cols-1 gap-2">
                {options
                  .filter(opt => !formData.option_ids.includes(opt.id))
                  .filter(opt =>
                    opt.name.toLowerCase().includes(optionSearchQuery.toLowerCase()) ||
                    (opt.internal_comment && opt.internal_comment.toLowerCase().includes(optionSearchQuery.toLowerCase()))
                  )
                  .map((option) => (
                    <label
                      key={option.id}
                      className="flex items-start space-x-3 p-2 rounded cursor-pointer transition-colors hover:bg-gray-50 border border-transparent hover:border-gray-200"
                    >
                      <input
                        type="checkbox"
                        checked={false}
                        onChange={(e) => handleOptionToggle(option, e.target.checked)}
                        className="w-4 h-4 mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center flex-wrap gap-1">
                          <span className="font-semibold text-sm">{option.name}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            option.is_required
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {option.is_required ? 'Obligatoire' : 'Optionnel'}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({option.choices?.length || 0} choix)
                          </span>
                        </div>
                        {option.internal_comment && (
                          <p className="text-xs text-yellow-700 italic mt-1">
                            📝 {option.internal_comment}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                {options
                  .filter(opt => !formData.option_ids.includes(opt.id))
                  .filter(opt =>
                    opt.name.toLowerCase().includes(optionSearchQuery.toLowerCase()) ||
                    (opt.internal_comment && opt.internal_comment.toLowerCase().includes(optionSearchQuery.toLowerCase()))
                  )
                  .length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {optionSearchQuery ? (
                      <>
                        <p className="text-sm">Aucune option trouvée pour "{optionSearchQuery}"</p>
                        <button
                          type="button"
                          onClick={() => setOptionSearchQuery('')}
                          className="text-xs text-primary hover:underline mt-2"
                        >
                          Effacer la recherche
                        </button>
                      </>
                    ) : (
                      <p className="text-sm">Toutes les options sont déjà sélectionnées</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Disponibilité avec explications */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold mb-3">Disponibilité du produit</h4>
          
          <div className="space-y-3">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_available}
                onChange={handleAvailabilityChange}
                className="w-4 h-4 mt-1 text-primary rounded"
              />
              <div>
                <span className="text-sm font-semibold text-gray-700">Visible dans l'app</span>
                <p className="text-xs text-gray-500 mt-1">
                  Si coché, le produit apparaît dans l'application mobile
                </p>
              </div>
            </label>

            <label className={`flex items-start space-x-3 cursor-pointer ${
              !formData.is_available ? 'opacity-50' : ''
            }`}>
              <input
                type="checkbox"
                checked={formData.is_out_of_stock}
                onChange={handleOutOfStockChange}
                disabled={!formData.is_available}
                className="w-4 h-4 mt-1 text-primary rounded"
              />
              <div>
                <span className="text-sm font-semibold text-gray-700">Rupture de stock</span>
                <p className="text-xs text-gray-500 mt-1">
                  Si coché, affiche une bannière "Indisponible pour le moment" dans l'app
                </p>
              </div>
            </label>

            {/* Boutons rapides rupture */}
            {formData.is_out_of_stock && (
              <div className="ml-7 mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const now = new Date();
                    const resumeAt = new Date(now.getTime() + 2 * 60 * 60 * 1000).toISOString();
                    setFormData({...formData, stock_status: '2h', stock_resume_at: resumeAt});
                  }}
                  className={`px-3 py-1 text-xs rounded-full font-medium border-2 transition-colors ${
                    formData.stock_status === '2h'
                      ? 'bg-orange-100 border-orange-500 text-orange-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-orange-400'
                  }`}
                >
                  ⏱ Rupture 2h
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const now = new Date();
                    // Fin de journée (minuit = début du jour suivant)
                    const tomorrow = new Date(now);
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    tomorrow.setHours(0, 0, 0, 0);
                    const resumeAt = tomorrow.toISOString();
                    setFormData({...formData, stock_status: 'today', stock_resume_at: resumeAt});
                  }}
                  className={`px-3 py-1 text-xs rounded-full font-medium border-2 transition-colors ${
                    formData.stock_status === 'today'
                      ? 'bg-red-100 border-red-500 text-red-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-red-400'
                  }`}
                >
                  📅 Rupture journée
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, stock_status: 'indefinite', stock_resume_at: null})}
                  className={`px-3 py-1 text-xs rounded-full font-medium border-2 transition-colors ${
                    formData.stock_status === 'indefinite'
                      ? 'bg-gray-100 border-gray-500 text-gray-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
                >
                  ⛔ Rupture indéfinie
                </button>
              </div>
            )}
          </div>
          
          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
            ℹ️ <strong>Note :</strong> {
              !formData.is_available 
                ? 'Le produit est masqué de l\'application' 
                : formData.is_out_of_stock
                  ? 'Le produit est visible mais marqué comme indisponible'
                  : 'Le produit est visible et disponible à la commande'
            }
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button type="button" variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Enregistrement...' : product ? 'Mettre à jour' : 'Créer'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
